// Copyright (c) Meta Platforms, Inc. and affiliates.
'use client';

/**
 * @file AvatarGroupOverflow.tsx
 * @input Uses React, StyleX, AvatarGroupContext, i18n (useTranslator)
 * @output Exports AvatarGroupOverflow for overflow indicator
 * @position Slot component used inside AvatarGroup
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/AvatarGroup/AvatarGroup.doc.mjs
 * - /packages/core/src/AvatarGroup/index.ts
 * - /packages/cli/assets/templates/blocks/components/AvatarGroup/ (showcase blocks)
 */
import React from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, typographyVars, fontWeightVars, radiusVars, spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { useAvatarGroup } from "./AvatarGroupContext.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx } from "react/jsx-runtime";
const BORDER_WIDTH = 2;
const OVERFLOW_FONT_RATIO = 0.35;
const styles = {
  base: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kaIpWk: "xjspbzw",
    kWkggS: "x10xzikg",
    kMwMTN: "xv1l7n4",
    kMv6JI: "x9ynric",
    k63SB2: "x1e4wzip",
    kfSwDN: "x87ps6o",
    kMzoRj: "xdh2fpr",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1touxvs",
    kB7OPa: "x9f619",
    kg3NbH: "xf314gf",
    kKwaWg: "x14bno8m",
    $$css: true
  },
  button: {
    kkrTdU: "x1ypdohk",
    k8WAf4: "xt970qd",
    kKwaWg: "x14bno8m xbfmc0r x1nocapi",
    kI3sdo: "x1a2a7pz x17nn4n9",
    kInvED: "x7s97pk",
    $$css: true
  },
  overlap: {
    keTefX: "x13hpdyo",
    $$css: true
  }
};
const _temp = {
  k7Eaqz: "xkj4a21",
  kZKoxP: "x16ye13r",
  "$$css": true
};
const _temp2 = {
  kGuDYH: "xdmh292",
  "$$css": true
};
const _temp3 = {
  "--_avatar-group-overlap": "xlz5hwt",
  "$$css": true
};
const dynamicStyles = {
  size: s => [_temp, {
    "--x-minWidth": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(s + BORDER_WIDTH * 2),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(s + BORDER_WIDTH * 2)
  }],
  fontSize: s => [_temp2, {
    "--x-fontSize": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(s * OVERFLOW_FONT_RATIO)
  }],
  overlap: offset => [_temp3, {
    "--x---_avatar-group-overlap": `${offset}px` != null ? `${offset}px` : undefined
  }]
};

/**
 * Overflow indicator for AvatarGroup. Shows a "+N" count and
 * optionally handles clicks.
 *
 * @example
 * ```
 * <AvatarGroup size="lg">
 *   {users.slice(0, 3).map(u => (
 *     <Avatar key={u.id} src={u.src} name={u.name} />
 *   ))}
 *   <AvatarGroupOverflow count={users.length - 3} onClick={showAll} />
 * </AvatarGroup>
 * ```
 */
export function AvatarGroupOverflow({
  ref,
  count,
  onClick,
  children,
  xstyle,
  className,
  style,
  ...rest
}) {
  const t = useTranslator();
  const group = useAvatarGroup();
  const numericSize = group?.numericSize ?? 36;
  const overlap = group?.overlap ?? 0;
  const label = t('@astryx.avatarGroup.overflow', {
    count
  });
  const content = children ?? `+${count}`;
  if (onClick) {
    return /*#__PURE__*/_jsx("button", {
      ref: ref,
      type: "button",
      onClick: onClick,
      ...rest,
      "aria-label": label,
      "data-avatar-item": "",
      ...mergeProps(themeProps('avatar-group-overflow'), stylex.props(styles.base, styles.button, styles.overlap, dynamicStyles.size(numericSize), dynamicStyles.fontSize(numericSize), dynamicStyles.overlap(-overlap), xstyle), className, style),
      children: content
    });
  }
  return /*#__PURE__*/_jsx("span", {
    ref: ref,
    ...rest,
    "aria-label": label,
    ...mergeProps(themeProps('avatar-group-overflow'), stylex.props(styles.base, styles.overlap, dynamicStyles.size(numericSize), dynamicStyles.fontSize(numericSize), dynamicStyles.overlap(-overlap), xstyle), className, style),
    children: content
  });
}
AvatarGroupOverflow.displayName = 'AvatarGroupOverflow';